// 📊 STATISTICHE V2 - Componenti React
// Visualizzazione completa statistiche: Giocatore, Torneo, Club

import React, { useState, useEffect, useCallback } from 'react';
import { TrendPuntiChart, SezioneConfrontoGiocatori, TopMarcatoriChart, DistribuzioneRisultatiChart, StoricoStagioniChart } from './components/Charts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// ============================================
// 🎨 HELPERS
// ============================================

const FormaIndicator = ({ forma }) => {
  if (!forma || forma.length === 0) return <span className="text-gray-400">-</span>;
  
  return (
    <div className="flex gap-1">
      {forma.map((r, i) => (
        <span 
          key={i}
          className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
            r === 'V' ? 'bg-green-500 text-white' :
            r === 'N' ? 'bg-gray-400 text-white' :
            'bg-red-500 text-white'
          }`}
        >
          {r}
        </span>
      ))}
    </div>
  );
};

const StatCard = ({ icon, label, value, subvalue, color = 'blue' }) => (
  <div className={`bg-${color}-50 rounded-xl p-4 text-center`}>
    <div className="text-2xl mb-1">{icon}</div>
    <div className={`text-2xl font-bold text-${color}-600`}>{value}</div>
    <div className="text-xs text-gray-500">{label}</div>
    {subvalue && <div className="text-xs text-gray-400 mt-1">{subvalue}</div>}
  </div>
);

const ProgressBar = ({ value, max, color = 'blue' }) => {
  const percent = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div 
        className={`bg-${color}-500 h-2 rounded-full transition-all`}
        style={{ width: `${percent}%` }}
      />
    </div>
  );
};


// ============================================
// 👤 SCHEDA GIOCATORE COMPLETA
// ============================================

export const SchedaGiocatoreV2 = ({ nomeGiocatore, socioId, token, stagione, onClose }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('generale');

  const api = useCallback(async (url) => {
    try {
      const res = await fetch(`${BACKEND_URL}${url}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      return res.ok ? await res.json() : null;
    } catch (err) {
      console.error('API Error:', err);
      return null;
    }
  }, [token]);

  useEffect(() => {
    const loadStats = async () => {
      setLoading(true);
      setError(null);
      
      let url = `/api/club-stats/v2/giocatore/${encodeURIComponent(nomeGiocatore)}`;
      if (socioId) url += `?socio_id=${socioId}`;
      if (stagione) url += `${socioId ? '&' : '?'}stagione=${stagione}`;
      
      const data = await api(url);
      
      if (data?.success && data.statistiche) {
        setStats(data.statistiche);
      } else {
        setError('Nessuna statistica disponibile');
      }
      setLoading(false);
    };
    
    loadStats();
  }, [api, nomeGiocatore, socioId, stagione]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 text-center">
          <div className="animate-spin text-4xl mb-4">⚽</div>
          <p>Caricamento statistiche...</p>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-8 text-center max-w-md">
          <div className="text-4xl mb-4">📊</div>
          <p className="text-gray-600 mb-4">{error || 'Nessuna statistica'}</p>
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded-lg">
            Chiudi
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'generale', label: '📊 Generale' },
    { id: 'trend', label: '📈 Trend' },
    { id: 'palmares', label: '🏆 Palmares' },
    { id: 'record', label: '🎯 Record' },
    { id: 'avversari', label: '🆚 Avversari' }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[95vh] overflow-hidden flex flex-col my-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold">{stats.nome}</h2>
              <p className="text-sm opacity-80">
                {stagione ? `Stagione ${stagione}` : 'Tutte le stagioni'}
              </p>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white text-2xl">
              ✕
            </button>
          </div>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-4 mt-6">
            <div className="text-center">
              <div className="text-3xl font-bold">{stats.partite_giocate}</div>
              <div className="text-xs opacity-80">Partite</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-300">{stats.percentuale_vittorie}%</div>
              <div className="text-xs opacity-80">Vittorie</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-300">{stats.punti_ranking_totali}</div>
              <div className="text-xs opacity-80">Punti Ranking</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-300">{stats.tornei_vinti}</div>
              <div className="text-xs opacity-80">Tornei Vinti</div>
            </div>
          </div>
          
          {/* Forma */}
          <div className="mt-4 flex items-center gap-3">
            <span className="text-sm opacity-80">Forma:</span>
            <FormaIndicator forma={stats.forma} />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-4 py-3 text-sm font-medium ${
                activeTab === tab.id 
                  ? 'border-b-2 border-blue-500 text-blue-600 bg-blue-50' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* TAB GENERALE */}
          {activeTab === 'generale' && (
            <div className="space-y-6">
              {/* Rendimento */}
              <div>
                <h3 className="font-bold text-lg mb-4">📊 Rendimento</h3>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="bg-green-50 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-green-600">{stats.vinte}</div>
                    <div className="text-sm text-gray-500">Vittorie</div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-gray-600">{stats.pareggiate}</div>
                    <div className="text-sm text-gray-500">Pareggi</div>
                  </div>
                  <div className="bg-red-50 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-red-600">{stats.perse}</div>
                    <div className="text-sm text-gray-500">Sconfitte</div>
                  </div>
                </div>
                
                {/* Barra rendimento */}
                <div className="flex h-4 rounded-full overflow-hidden bg-gray-200">
                  <div 
                    className="bg-green-500 transition-all"
                    style={{ width: `${stats.partite_giocate > 0 ? (stats.vinte / stats.partite_giocate) * 100 : 0}%` }}
                  />
                  <div 
                    className="bg-gray-400 transition-all"
                    style={{ width: `${stats.partite_giocate > 0 ? (stats.pareggiate / stats.partite_giocate) * 100 : 0}%` }}
                  />
                  <div 
                    className="bg-red-500 transition-all"
                    style={{ width: `${stats.partite_giocate > 0 ? (stats.perse / stats.partite_giocate) * 100 : 0}%` }}
                  />
                </div>
              </div>

              {/* Gol */}
              <div>
                <h3 className="font-bold text-lg mb-4">⚽ Statistiche Gol</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">{stats.gol_fatti}</div>
                    <div className="text-xs text-gray-500">Gol Fatti</div>
                    <div className="text-xs text-gray-400">media {stats.media_gol_fatti}</div>
                  </div>
                  <div className="bg-orange-50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-orange-600">{stats.gol_subiti}</div>
                    <div className="text-xs text-gray-500">Gol Subiti</div>
                    <div className="text-xs text-gray-400">media {stats.media_gol_subiti}</div>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-4 text-center">
                    <div className={`text-2xl font-bold ${stats.differenza_reti >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {stats.differenza_reti >= 0 ? '+' : ''}{stats.differenza_reti}
                    </div>
                    <div className="text-xs text-gray-500">Diff. Reti</div>
                  </div>
                  <div className="bg-teal-50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-teal-600">{stats.record?.clean_sheets || 0}</div>
                    <div className="text-xs text-gray-500">Clean Sheets</div>
                  </div>
                </div>
              </div>

              {/* Tornei */}
              <div>
                <h3 className="font-bold text-lg mb-4">🏆 Tornei</h3>
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-700">{stats.tornei_giocati}</div>
                    <div className="text-xs text-gray-500">Giocati</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-500">{stats.tornei_vinti}</div>
                    <div className="text-xs text-gray-500">🥇 Vinti</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-400">{stats.secondi_posti}</div>
                    <div className="text-xs text-gray-500">🥈 Secondi</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-400">{stats.terzi_posti}</div>
                    <div className="text-xs text-gray-500">🥉 Terzi</div>
                  </div>
                </div>
              </div>

              {/* Grafico Distribuzione Risultati */}
              <div className="border-t pt-6">
                <DistribuzioneRisultatiChart 
                  vinte={stats.vinte}
                  pareggiate={stats.pareggiate}
                  perse={stats.perse}
                  nomeGiocatore={stats.nome}
                />
              </div>
            </div>
          )}

          {/* TAB TREND - F17 Grafici Interattivi */}
          {activeTab === 'trend' && (
            <div className="space-y-4">
              {(stats.palmares && stats.palmares.length > 0) || (stats.dettaglio_tornei && stats.dettaglio_tornei.length > 0) ? (
                <TrendPuntiChart 
                  data={stats.palmares} 
                  dettaglioTornei={stats.dettaglio_tornei}
                  nomeGiocatore={stats.nome}
                />
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-2">📈</div>
                  <p>Nessun dato disponibile per il trend</p>
                  <p className="text-sm mt-2">Partecipa ai tornei per vedere il tuo andamento!</p>
                </div>
              )}
            </div>
          )}

          {/* TAB PALMARES */}
          {activeTab === 'palmares' && (
            <div className="space-y-4">
              <h3 className="font-bold text-lg">🏆 Palmares Completo</h3>
              
              {(!stats.palmares || stats.palmares.length === 0) ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-2">🏆</div>
                  <p>Nessun palmares disponibile</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {stats.palmares.map((p, i) => (
                    <div key={i} className="flex items-center gap-4 bg-gray-50 rounded-xl p-4">
                      <span className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${
                        p.posizione === 1 ? 'bg-yellow-400 text-yellow-900' :
                        p.posizione === 2 ? 'bg-gray-300 text-gray-700' :
                        p.posizione === 3 ? 'bg-orange-300 text-orange-800' :
                        'bg-gray-200 text-gray-600'
                      }`}>
                        {p.posizione}°
                      </span>
                      <div className="flex-1">
                        <div className="font-medium">{p.torneo}</div>
                        <div className="text-xs text-gray-500">{p.stagione}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-green-600">+{p.punti_ranking} pt</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB RECORD */}
          {activeTab === 'record' && (
            <div className="space-y-4">
              <h3 className="font-bold text-lg">🎯 Record Personali</h3>
              
              <div className="grid gap-4">
                {/* Vittoria più larga */}
                {stats.record?.vittoria_piu_larga && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">🎯</span>
                      <div>
                        <div className="font-bold text-green-700">Vittoria più larga</div>
                        <div className="text-lg font-bold">{stats.record.vittoria_piu_larga.risultato}</div>
                        <div className="text-sm text-gray-500">
                          vs {stats.record.vittoria_piu_larga.avversario} • {stats.record.vittoria_piu_larga.torneo}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Partita con più gol */}
                {stats.record?.partita_piu_gol && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">⚽</span>
                      <div>
                        <div className="font-bold text-blue-700">Partita con più gol</div>
                        <div className="text-lg font-bold">{stats.record.partita_piu_gol.risultato}</div>
                        <div className="text-sm text-gray-500">
                          vs {stats.record.partita_piu_gol.avversario} • {stats.record.partita_piu_gol.totale_gol} gol totali
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Serie record */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-purple-600">{stats.record?.serie_vittorie_max || 0}</div>
                    <div className="text-sm text-gray-600">🔥 Serie Vittorie Max</div>
                  </div>
                  <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-teal-600">{stats.record?.serie_imbattibilita_max || 0}</div>
                    <div className="text-sm text-gray-600">🛡️ Serie Imbattibilità Max</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB AVVERSARI */}
          {activeTab === 'avversari' && (
            <div className="space-y-4">
              <h3 className="font-bold text-lg">🆚 Testa a Testa</h3>
              
              {(!stats.testa_a_testa || stats.testa_a_testa.length === 0) ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-2">🆚</div>
                  <p>Nessuno scontro diretto registrato</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {stats.testa_a_testa.map((t, i) => (
                    <div key={i} className="bg-gray-50 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{t.avversario}</span>
                        <span className="text-sm text-gray-500">{t.partite_totali} partite</span>
                      </div>
                      <div className="flex gap-2 text-sm">
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                          {t.vinte}V
                        </span>
                        <span className="px-2 py-1 bg-gray-200 text-gray-600 rounded">
                          {t.pareggiate}N
                        </span>
                        <span className="px-2 py-1 bg-red-100 text-red-700 rounded">
                          {t.perse}P
                        </span>
                        <span className="ml-auto text-gray-500">
                          Gol: {t.gol_fatti}-{t.gol_subiti}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ============================================
// 📊 STATISTICHE TORNEO TAB
// ============================================

export const StatisticheTorneoV2 = ({ torneoId, token }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const api = useCallback(async (url) => {
    try {
      const res = await fetch(`${BACKEND_URL}${url}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      return res.ok ? await res.json() : null;
    } catch (err) {
      console.error('API Error:', err);
      return null;
    }
  }, [token]);

  useEffect(() => {
    const loadStats = async () => {
      setLoading(true);
      const data = await api(`/api/club-stats/v2/torneo/${torneoId}`);
      if (data?.success) {
        setStats(data.statistiche);
      }
      setLoading(false);
    };
    loadStats();
  }, [api, torneoId]);

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Caricamento statistiche...</div>;
  }

  if (!stats) {
    return (
      <div className="text-center py-8 text-gray-500">
        <div className="text-4xl mb-2">📊</div>
        <p>Nessuna statistica disponibile</p>
        <p className="text-sm">Inserisci risultati per vedere le statistiche</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">{stats.partite_giocate}</div>
          <div className="text-xs text-gray-500">Partite</div>
        </div>
        <div className="bg-green-50 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{stats.gol_totali}</div>
          <div className="text-xs text-gray-500">Gol Totali</div>
        </div>
        <div className="bg-purple-50 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-purple-600">{stats.media_gol_partita}</div>
          <div className="text-xs text-gray-500">Media Gol/Partita</div>
        </div>
        <div className="bg-orange-50 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-orange-600">{stats.num_partecipanti}</div>
          <div className="text-xs text-gray-500">Partecipanti</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Classifica Marcatori */}
        <div className="bg-white border rounded-xl p-4">
          <h4 className="font-bold mb-4">⚽ Classifica Marcatori</h4>
          {stats.classifica_marcatori?.length > 0 ? (
            <div className="space-y-2">
              {stats.classifica_marcatori.slice(0, 5).map((m, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    i === 0 ? 'bg-yellow-400 text-yellow-900' :
                    i === 1 ? 'bg-gray-300 text-gray-700' :
                    i === 2 ? 'bg-orange-300 text-orange-800' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {i + 1}
                  </span>
                  <span className="flex-1 font-medium">{m.nome}</span>
                  <span className="font-bold text-blue-600">{m.gol} gol</span>
                  <span className="text-xs text-gray-400">({m.media}/p)</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">Nessun dato</p>
          )}
        </div>

        {/* Miglior Difesa */}
        <div className="bg-white border rounded-xl p-4">
          <h4 className="font-bold mb-4">🧤 Miglior Difesa</h4>
          {stats.miglior_difesa?.length > 0 ? (
            <div className="space-y-2">
              {stats.miglior_difesa.slice(0, 5).map((d, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    i === 0 ? 'bg-green-400 text-green-900' :
                    i === 1 ? 'bg-green-200 text-green-700' :
                    i === 2 ? 'bg-green-100 text-green-600' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {i + 1}
                  </span>
                  <span className="flex-1 font-medium">{d.nome}</span>
                  <span className="font-bold text-green-600">{d.gol_subiti} subiti</span>
                  <span className="text-xs text-gray-400">({d.media}/p)</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">Nessun dato</p>
          )}
        </div>
      </div>

      {/* Risultati Frequenti */}
      <div className="bg-white border rounded-xl p-4">
        <h4 className="font-bold mb-4">📊 Risultati più Frequenti</h4>
        {stats.risultati_frequenti?.length > 0 ? (
          <div className="flex flex-wrap gap-3">
            {stats.risultati_frequenti.slice(0, 6).map((r, i) => (
              <div key={i} className="bg-gray-100 rounded-lg px-4 py-2 text-center">
                <div className="font-bold text-lg">{r.risultato}</div>
                <div className="text-xs text-gray-500">{r.volte}x ({r.percentuale}%)</div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm">Nessun dato</p>
        )}
      </div>
    </div>
  );
};


// ============================================
// 🏢 DASHBOARD STATISTICHE CLUB
// ============================================

export const DashboardStatsClubV2 = ({ token, onSelectGiocatore }) => {
  const [stats, setStats] = useState(null);
  const [storico, setStorico] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stagione, setStagione] = useState('');
  const [stagioniDisponibili, setStagioniDisponibili] = useState([]);
  const [activeTab, setActiveTab] = useState('classifica');

  const api = useCallback(async (url) => {
    try {
      const res = await fetch(`${BACKEND_URL}${url}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      return res.ok ? await res.json() : null;
    } catch (err) {
      console.error('API Error:', err);
      return null;
    }
  }, [token]);

  const loadData = useCallback(async () => {
    setLoading(true);
    
    // Load stagioni
    const stagioniData = await api('/api/clubs/stagioni-disponibili');
    if (stagioniData) {
      setStagioniDisponibili(stagioniData.stagioni || []);
    }
    
    // Load stats
    const url = stagione 
      ? `/api/club-stats/v2/club?stagione=${stagione}`
      : '/api/club-stats/v2/club';
    const statsData = await api(url);
    if (statsData?.success) {
      setStats(statsData.statistiche);
    }
    
    // Load storico
    const storicoData = await api('/api/club-stats/v2/storico-stagioni');
    if (storicoData?.success) {
      setStorico(storicoData.storico || []);
    }
    
    setLoading(false);
  }, [api, stagione]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Caricamento statistiche club...</div>;
  }

  if (!stats) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-8 text-center">
        <div className="text-4xl mb-2">📊</div>
        <h3 className="font-bold text-blue-800 mb-2">Nessuna statistica disponibile</h3>
        <p className="text-blue-700 text-sm">Crea tornei V2 e inserisci risultati per vedere le statistiche.</p>
      </div>
    );
  }

  const tabs = [
    { id: 'classifica', label: '🏅 Classifica' },
    { id: 'confronto', label: '🎯 Confronto' },
    { id: 'marcatori', label: '⚽ Marcatori' },
    { id: 'record', label: '🎯 Record' },
    { id: 'storico', label: '📅 Storico' }
  ];

  return (
    <div className="space-y-6">
      {/* Header con filtro stagione */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold">📊 Statistiche Club V2</h2>
          <p className="text-sm text-gray-500">
            {stats.tornei_completati} tornei completati • {stats.partite_totali} partite • {stats.gol_totali} gol
          </p>
        </div>
        <select 
          value={stagione} 
          onChange={e => setStagione(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm"
        >
          <option value="">Tutte le stagioni</option>
          {stagioniDisponibili.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white text-center">
          <div className="text-3xl font-bold">{stats.tornei_completati}</div>
          <div className="text-sm opacity-80">Tornei Completati</div>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white text-center">
          <div className="text-3xl font-bold">{stats.partite_totali}</div>
          <div className="text-sm opacity-80">Partite Totali</div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white text-center">
          <div className="text-3xl font-bold">{stats.gol_totali}</div>
          <div className="text-sm opacity-80">Gol Totali</div>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 text-white text-center">
          <div className="text-3xl font-bold">{stats.media_gol_partita}</div>
          <div className="text-sm opacity-80">Media Gol/Partita</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="flex border-b overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-4 py-3 text-sm font-medium whitespace-nowrap ${
                activeTab === tab.id 
                  ? 'border-b-2 border-blue-500 text-blue-600 bg-blue-50' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* TAB CLASSIFICA */}
          {activeTab === 'classifica' && (
            <div>
              <h3 className="font-bold text-lg mb-4">🏅 Classifica Stagionale</h3>
              {stats.classifica_stagionale?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm">#</th>
                        <th className="px-4 py-3 text-left text-sm">Giocatore</th>
                        <th className="px-4 py-3 text-center text-sm">Tornei</th>
                        <th className="px-4 py-3 text-center text-sm">🥇</th>
                        <th className="px-4 py-3 text-center text-sm">🥈</th>
                        <th className="px-4 py-3 text-center text-sm">🥉</th>
                        <th className="px-4 py-3 text-center text-sm font-bold">Punti</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.classifica_stagionale.map((c, i) => (
                        <tr 
                          key={i} 
                          className={`border-b hover:bg-blue-50 cursor-pointer ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                          onClick={() => onSelectGiocatore && onSelectGiocatore(c.nome, c.socio_id)}
                        >
                          <td className="px-4 py-3">
                            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                              i === 0 ? 'bg-yellow-400 text-yellow-900' :
                              i === 1 ? 'bg-gray-300 text-gray-700' :
                              i === 2 ? 'bg-orange-300 text-orange-800' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {c.posizione}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-medium">{c.nome}</td>
                          <td className="px-4 py-3 text-center text-gray-500">{c.tornei_giocati}</td>
                          <td className="px-4 py-3 text-center text-yellow-600 font-bold">{c.tornei_vinti || 0}</td>
                          <td className="px-4 py-3 text-center text-gray-500">{c.secondi_posti || 0}</td>
                          <td className="px-4 py-3 text-center text-orange-500">{c.terzi_posti || 0}</td>
                          <td className="px-4 py-3 text-center font-bold text-blue-600">{c.punti_ranking_totali}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">Nessuna classifica disponibile</p>
              )}
            </div>
          )}

          {/* TAB CONFRONTO - F17 Grafici Interattivi */}
          {activeTab === 'confronto' && (
            <div>
              <SezioneConfrontoGiocatori 
                token={token} 
                clubStats={stats}
              />
            </div>
          )}

          {/* TAB MARCATORI */}
          {activeTab === 'marcatori' && (
            <div className="space-y-6">
              {/* Grafico BarChart */}
              <div className="bg-white rounded-xl shadow p-6">
                <TopMarcatoriChart 
                  data={stats.top_marcatori || stats.classifica_stagionale} 
                  maxItems={10} 
                />
              </div>
              
              {/* Lista cliccabile */}
              <div className="bg-white rounded-xl shadow p-6">
                <h4 className="font-bold text-gray-700 mb-4">📋 Lista Completa</h4>
                {stats.top_marcatori?.length > 0 ? (
                  <div className="space-y-2">
                    {stats.top_marcatori.map((m, i) => (
                      <div 
                        key={i} 
                        className="flex items-center gap-4 bg-gray-50 rounded-lg p-3 cursor-pointer hover:bg-purple-50 transition-colors"
                        onClick={() => onSelectGiocatore && onSelectGiocatore(m.nome)}
                      >
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          i === 0 ? 'bg-yellow-400 text-yellow-900' :
                          i === 1 ? 'bg-gray-300 text-gray-700' :
                          i === 2 ? 'bg-orange-300 text-orange-800' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {i + 1}
                        </span>
                        <span className="flex-1 font-medium">{m.nome}</span>
                        <span className="text-xl font-bold text-purple-600">{m.gol}</span>
                        <span className="text-xs text-gray-400">⚽</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">Nessun marcatore</p>
                )}
              </div>
            </div>
          )}

          {/* TAB RECORD */}
          {activeTab === 'record' && (
            <div className="space-y-4">
              <h3 className="font-bold text-lg mb-4">🎯 Record del Club</h3>
              
              {stats.record_club?.partita_piu_gol && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">⚽</span>
                    <div>
                      <div className="font-bold text-blue-700">Partita con più gol</div>
                      <div className="text-xl font-bold">{stats.record_club.partita_piu_gol.risultato}</div>
                      <div className="text-sm text-gray-600">
                        {stats.record_club.partita_piu_gol.giocatori} • {stats.record_club.partita_piu_gol.torneo}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {stats.record_club?.vittoria_piu_larga && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">🎯</span>
                    <div>
                      <div className="font-bold text-green-700">Vittoria più larga</div>
                      <div className="text-xl font-bold">{stats.record_club.vittoria_piu_larga.risultato}</div>
                      <div className="text-sm text-gray-600">
                        {stats.record_club.vittoria_piu_larga.vincitore} vs {stats.record_club.vittoria_piu_larga.perdente}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {stats.record_club?.capocannoniere_stagione && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">👑</span>
                    <div>
                      <div className="font-bold text-yellow-700">Capocannoniere</div>
                      <div className="text-xl font-bold">{stats.record_club.capocannoniere_stagione.nome}</div>
                      <div className="text-sm text-gray-600">
                        {stats.record_club.capocannoniere_stagione.gol} gol
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {stats.record_club?.piu_tornei_vinti && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">🏆</span>
                    <div>
                      <div className="font-bold text-purple-700">Più tornei vinti</div>
                      <div className="text-xl font-bold">{stats.record_club.piu_tornei_vinti.nome}</div>
                      <div className="text-sm text-gray-600">
                        {stats.record_club.piu_tornei_vinti.tornei} tornei
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB STORICO */}
          {activeTab === 'storico' && (
            <div>
              <h3 className="font-bold text-lg mb-4">📅 Storico Stagioni</h3>
              {storico.length > 0 ? (
                <div className="space-y-4">
                  {storico.map((s, i) => (
                    <div key={i} className="border rounded-xl p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-bold text-lg">{s.stagione}</h4>
                          <p className="text-sm text-gray-500">
                            {s.tornei} tornei • {s.partite} partite • {s.gol} gol
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-4">
                        {s.vincitore_stagione && (
                          <div className="flex items-center gap-2">
                            <span className="text-yellow-500">🏆</span>
                            <span className="font-medium">{s.vincitore_stagione.nome}</span>
                            <span className="text-sm text-gray-400">
                              ({s.vincitore_stagione.punti_ranking_totali} pt)
                            </span>
                          </div>
                        )}
                        {s.capocannoniere && (
                          <div className="flex items-center gap-2">
                            <span className="text-blue-500">⚽</span>
                            <span className="font-medium">{s.capocannoniere.nome}</span>
                            <span className="text-sm text-gray-400">
                              ({s.capocannoniere.gol} gol)
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">Nessuno storico disponibile</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ============================================
// 🏆 TORNEI CONCLUSI TAB
// ============================================

const TorneiConclusiTab = ({ token, onSelectTorneo }) => {
  const [tornei, setTornei] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadTornei = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${BACKEND_URL}/api/club-stats/v2/tornei-conclusi`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        
        if (data.success) {
          setTornei(data.tornei || []);
        } else {
          setError('Errore caricamento tornei');
        }
      } catch (err) {
        console.error('Error loading tornei conclusi:', err);
        setError('Errore di connessione');
      } finally {
        setLoading(false);
      }
    };

    loadTornei();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full"></div>
        <span className="ml-3 text-gray-500">Caricamento tornei...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  if (tornei.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">🏆</div>
        <h3 className="text-xl font-semibold text-gray-700 mb-2">Nessun torneo concluso</h3>
        <p className="text-gray-500">I tornei completati appariranno qui con il podio finale.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="tornei-conclusi-tab">
      <p className="text-sm text-gray-500 mb-4">
        {tornei.length} torneo{tornei.length !== 1 ? 'i' : ''} conclus{tornei.length !== 1 ? 'i' : 'o'}
      </p>
      
      {tornei.map((torneo) => (
        <div 
          key={torneo.id}
          className="bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all cursor-pointer"
          onClick={() => onSelectTorneo && onSelectTorneo(torneo.id, torneo.importato)}
          data-testid={`torneo-${torneo.id}`}
        >
          <div className="flex items-start justify-between">
            {/* Info Torneo */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-bold text-gray-800">{torneo.nome}</h3>
                {torneo.importato && (
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                    Importato
                  </span>
                )}
              </div>
              
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                <span>📅 {torneo.anno}</span>
                <span>👥 {torneo.partecipanti} partecipanti</span>
                <span>⚽ {torneo.partite_giocate} partite</span>
              </div>

              {/* Podio */}
              <div className="flex items-center gap-6">
                {/* 1° Posto */}
                {torneo.vincitore && (
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">🥇</span>
                    <div>
                      <p className="font-bold text-gray-800">{torneo.vincitore.nome}</p>
                      <p className="text-xs text-gray-500">
                        {torneo.vincitore.punti} pt • {torneo.vincitore.gol_fatti} gol
                      </p>
                    </div>
                  </div>
                )}
                
                {/* 2° Posto */}
                {torneo.secondo && (
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">🥈</span>
                    <div>
                      <p className="font-semibold text-gray-700">{torneo.secondo.nome}</p>
                      <p className="text-xs text-gray-500">{torneo.secondo.punti} pt</p>
                    </div>
                  </div>
                )}
                
                {/* 3° Posto */}
                {torneo.terzo && (
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">🥉</span>
                    <div>
                      <p className="font-medium text-gray-600">{torneo.terzo.nome}</p>
                      <p className="text-xs text-gray-500">{torneo.terzo.punti} pt</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Arrow */}
            <div className="text-gray-400 text-xl">
              →
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};


// ============================================
// 🏆 F7: ALBO D'ORO - ARCHIVIO STORICO
// ============================================

export const AlboOro = ({ token, onSelectGiocatore, onSelectTorneo }) => {
  const [stagioni, setStagioni] = useState([]);
  const [campioni, setCampioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedStagione, setExpandedStagione] = useState(null);
  const [stagioneDetail, setStagioneDetail] = useState(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveStagione, setArchiveStagione] = useState('');
  const [archiving, setArchiving] = useState(false);
  const [activeTab, setActiveTab] = useState('tornei'); // 'tornei' | 'albo' | 'campioni' | 'storico'

  const api = useCallback(async (url, options = {}) => {
    const res = await fetch(`${BACKEND_URL}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
      }
    });
    return res.json();
  }, [token]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [alboRes, campioniRes] = await Promise.all([
        api('/api/club-stats/v2/albo-oro'),
        api('/api/club-stats/v2/campioni')
      ]);
      
      if (alboRes.success) {
        setStagioni(alboRes.stagioni || []);
      }
      if (campioniRes.success) {
        setCampioni(campioniRes.campioni || []);
      }
    } catch (err) {
      setError('Errore caricamento dati');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadStagioneDetail = async (stagione) => {
    if (expandedStagione === stagione) {
      setExpandedStagione(null);
      setStagioneDetail(null);
      return;
    }
    
    try {
      const res = await api(`/api/club-stats/v2/albo-oro/${encodeURIComponent(stagione)}`);
      if (res.success) {
        setStagioneDetail(res.stagione);
        setExpandedStagione(stagione);
      }
    } catch (err) {
      console.error('Errore caricamento dettaglio stagione:', err);
    }
  };

  const handleArchive = async () => {
    if (!archiveStagione.trim()) return;
    
    try {
      setArchiving(true);
      const res = await api('/api/club-stats/v2/archivia-stagione', {
        method: 'POST',
        body: JSON.stringify({ stagione: archiveStagione.trim() })
      });
      
      if (res.success) {
        setShowArchiveModal(false);
        setArchiveStagione('');
        loadData();
      } else {
        alert(res.detail || 'Errore archiviazione');
      }
    } catch (err) {
      console.error('Errore archiviazione:', err);
      alert('Errore durante l\'archiviazione');
    } finally {
      setArchiving(false);
    }
  };

  const handleDelete = async (stagione) => {
    if (!window.confirm(`Sei sicuro di voler eliminare l'archivio della stagione ${stagione}?`)) return;
    
    try {
      const res = await api(`/api/club-stats/v2/albo-oro/${encodeURIComponent(stagione)}`, {
        method: 'DELETE'
      });
      
      if (res.success) {
        loadData();
      } else {
        alert(res.detail || 'Errore eliminazione');
      }
    } catch (err) {
      console.error('Errore eliminazione:', err);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
        <div className="animate-spin w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full mx-auto"></div>
        <p className="mt-4 text-gray-500">Caricamento Albo d'Oro...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
        <p className="text-red-500">{error}</p>
        <button onClick={loadData} className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg">
          Riprova
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden" data-testid="albo-oro">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-500 to-amber-500 text-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              🏆 Albo d'Oro
            </h2>
            <p className="text-yellow-100 text-sm mt-1">
              Archivio storico delle stagioni
            </p>
          </div>
          <button
            onClick={() => setShowArchiveModal(true)}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-medium transition-colors"
            data-testid="archive-season-btn"
          >
            📥 Archivia Stagione
          </button>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2 mt-4 flex-wrap">
          <button
            onClick={() => setActiveTab('tornei')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'tornei' ? 'bg-white text-yellow-600' : 'bg-white/20 hover:bg-white/30'
            }`}
          >
            🏆 Tornei
          </button>
          <button
            onClick={() => setActiveTab('albo')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'albo' ? 'bg-white text-yellow-600' : 'bg-white/20 hover:bg-white/30'
            }`}
          >
            📅 Stagioni ({stagioni.length})
          </button>
          <button
            onClick={() => setActiveTab('storico')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'storico' ? 'bg-white text-yellow-600' : 'bg-white/20 hover:bg-white/30'
            }`}
          >
            📊 Grafici
          </button>
          <button
            onClick={() => setActiveTab('campioni')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'campioni' ? 'bg-white text-yellow-600' : 'bg-white/20 hover:bg-white/30'
            }`}
          >
            👑 Hall of Fame
          </button>
        </div>
      </div>

      <div className="p-6">
        {/* TAB TORNEI - Tornei conclusi */}
        {activeTab === 'tornei' && (
          <TorneiConclusiTab token={token} onSelectTorneo={onSelectTorneo} />
        )}

        {/* TAB STORICO - Grafici F17 Batch 3 */}
        {activeTab === 'storico' && (
          <div className="space-y-4">
            <StoricoStagioniChart stagioni={stagioni} />
          </div>
        )}

        {/* TAB ALBO - Lista stagioni */}
        {activeTab === 'albo' && (
          <div className="space-y-4">
            {stagioni.length > 0 ? (
              stagioni.map((s) => (
                <div 
                  key={s.id} 
                  className="border rounded-xl overflow-hidden"
                  data-testid={`stagione-${s.stagione}`}
                >
                  {/* Header stagione */}
                  <div 
                    className="bg-gray-50 p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => loadStagioneDetail(s.stagione)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span className="text-2xl font-bold text-gray-700">📅 {s.stagione}</span>
                        {s.campione && (
                          <div className="flex items-center gap-2">
                            <span className="text-yellow-500">🥇</span>
                            <span 
                              className="font-medium text-gray-800 hover:text-blue-600 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                onSelectGiocatore && onSelectGiocatore(s.campione.nome);
                              }}
                            >
                              {s.campione.nome}
                            </span>
                            <span className="text-sm text-gray-500">
                              ({s.campione.punti_ranking_totali} pt)
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-500">
                          {s.statistiche?.tornei_completati || 0} tornei
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(s.stagione);
                          }}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                          title="Elimina archivio"
                        >
                          🗑️
                        </button>
                        <span className={`transform transition-transform ${expandedStagione === s.stagione ? 'rotate-180' : ''}`}>
                          ▼
                        </span>
                      </div>
                    </div>
                    
                    {/* Podio preview */}
                    <div className="flex gap-6 mt-3">
                      {s.vice_campione && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <span className="text-gray-400">🥈</span>
                          <span>{s.vice_campione.nome}</span>
                        </div>
                      )}
                      {s.terzo && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <span className="text-orange-400">🥉</span>
                          <span>{s.terzo.nome}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Dettaglio espanso */}
                  {expandedStagione === s.stagione && stagioneDetail && (
                    <div className="p-4 border-t bg-white">
                      {/* Statistiche */}
                      <div className="grid grid-cols-4 gap-4 mb-6">
                        <div className="bg-blue-50 rounded-xl p-3 text-center">
                          <div className="text-2xl font-bold text-blue-600">
                            {stagioneDetail.statistiche?.tornei_completati || 0}
                          </div>
                          <div className="text-xs text-gray-500">Tornei</div>
                        </div>
                        <div className="bg-green-50 rounded-xl p-3 text-center">
                          <div className="text-2xl font-bold text-green-600">
                            {stagioneDetail.statistiche?.partite_totali || 0}
                          </div>
                          <div className="text-xs text-gray-500">Partite</div>
                        </div>
                        <div className="bg-purple-50 rounded-xl p-3 text-center">
                          <div className="text-2xl font-bold text-purple-600">
                            {stagioneDetail.statistiche?.gol_totali || 0}
                          </div>
                          <div className="text-xs text-gray-500">Gol totali</div>
                        </div>
                        <div className="bg-orange-50 rounded-xl p-3 text-center">
                          <div className="text-2xl font-bold text-orange-600">
                            {stagioneDetail.statistiche?.partecipanti_totali || 0}
                          </div>
                          <div className="text-xs text-gray-500">Giocatori</div>
                        </div>
                      </div>
                      
                      {/* Classifica completa */}
                      <h4 className="font-bold mb-3">📊 Classifica Completa</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left">#</th>
                              <th className="px-3 py-2 text-left">Giocatore</th>
                              <th className="px-3 py-2 text-center">Tornei</th>
                              <th className="px-3 py-2 text-center">🥇</th>
                              <th className="px-3 py-2 text-center">🥈</th>
                              <th className="px-3 py-2 text-center">🥉</th>
                              <th className="px-3 py-2 text-center font-bold">Punti</th>
                            </tr>
                          </thead>
                          <tbody>
                            {stagioneDetail.classifica_completa?.map((c, i) => (
                              <tr 
                                key={i} 
                                className={`border-b hover:bg-blue-50 cursor-pointer ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                                onClick={() => onSelectGiocatore && onSelectGiocatore(c.nome)}
                              >
                                <td className="px-3 py-2">
                                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                                    i === 0 ? 'bg-yellow-400 text-yellow-900' :
                                    i === 1 ? 'bg-gray-300 text-gray-700' :
                                    i === 2 ? 'bg-orange-300 text-orange-800' :
                                    'bg-gray-100 text-gray-600'
                                  }`}>
                                    {c.posizione}
                                  </span>
                                </td>
                                <td className="px-3 py-2 font-medium">{c.nome}</td>
                                <td className="px-3 py-2 text-center text-gray-500">{c.tornei_giocati}</td>
                                <td className="px-3 py-2 text-center text-yellow-600 font-bold">{c.tornei_vinti || 0}</td>
                                <td className="px-3 py-2 text-center text-gray-500">{c.secondi_posti || 0}</td>
                                <td className="px-3 py-2 text-center text-orange-500">{c.terzi_posti || 0}</td>
                                <td className="px-3 py-2 text-center font-bold text-blue-600">{c.punti_ranking_totali}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      {/* Capocannoniere */}
                      {stagioneDetail.capocannoniere && (
                        <div className="mt-4 p-3 bg-green-50 rounded-xl flex items-center gap-3">
                          <span className="text-2xl">⚽</span>
                          <div>
                            <div className="font-medium">Capocannoniere: {stagioneDetail.capocannoniere.nome}</div>
                            <div className="text-sm text-gray-500">{stagioneDetail.capocannoniere.gol} gol</div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-12 text-gray-500">
                <div className="text-6xl mb-4">📭</div>
                <p className="text-lg font-medium">Nessuna stagione archiviata</p>
                <p className="text-sm mt-2">Clicca su "Archivia Stagione" per salvare la classifica attuale</p>
              </div>
            )}
          </div>
        )}

        {/* TAB CAMPIONI - Hall of Fame */}
        {activeTab === 'campioni' && (
          <div>
            <h3 className="font-bold text-lg mb-4">👑 Hall of Fame - Campioni Storici</h3>
            {campioni.length > 0 ? (
              <div className="space-y-3">
                {campioni.map((c, i) => (
                  <div 
                    key={i}
                    className={`flex items-center justify-between p-4 rounded-xl cursor-pointer hover:shadow-md transition-shadow ${
                      i === 0 ? 'bg-gradient-to-r from-yellow-100 to-amber-100 border-2 border-yellow-400' :
                      i === 1 ? 'bg-gray-100' :
                      i === 2 ? 'bg-orange-50' :
                      'bg-white border'
                    }`}
                    onClick={() => onSelectGiocatore && onSelectGiocatore(c.nome)}
                  >
                    <div className="flex items-center gap-4">
                      <span className={`text-3xl font-bold ${
                        i === 0 ? 'text-yellow-600' :
                        i === 1 ? 'text-gray-500' :
                        i === 2 ? 'text-orange-500' :
                        'text-gray-400'
                      }`}>
                        {i + 1}°
                      </span>
                      <div>
                        <div className="font-bold text-lg">{c.nome}</div>
                        <div className="text-sm text-gray-500">
                          Stagioni vinte: {c.stagioni_vinte?.join(', ')}
                        </div>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className={`text-3xl font-bold ${
                        i === 0 ? 'text-yellow-600' : 'text-gray-700'
                      }`}>
                        {c.titoli} 🏆
                      </div>
                      <div className="text-xs text-gray-500">
                        {c.titoli === 1 ? 'titolo' : 'titoli'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <div className="text-6xl mb-4">👑</div>
                <p className="text-lg font-medium">Nessun campione ancora</p>
                <p className="text-sm mt-2">Archivia delle stagioni per popolare la Hall of Fame</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal Archivia Stagione */}
      {showArchiveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-xl font-bold mb-4">📥 Archivia Stagione</h3>
            <p className="text-gray-600 mb-4">
              Inserisci il nome della stagione da archiviare (es: "2024/25").
              Questo salverà la classifica attuale nell'Albo d'Oro.
            </p>
            <input
              type="text"
              value={archiveStagione}
              onChange={(e) => setArchiveStagione(e.target.value)}
              placeholder="Es: 2024/25"
              className="w-full px-4 py-3 border rounded-xl mb-4 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              data-testid="archive-stagione-input"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowArchiveModal(false)}
                className="flex-1 px-4 py-3 border rounded-xl hover:bg-gray-50"
                disabled={archiving}
              >
                Annulla
              </button>
              <button
                onClick={handleArchive}
                disabled={archiving || !archiveStagione.trim()}
                className="flex-1 px-4 py-3 bg-yellow-500 text-white rounded-xl hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                data-testid="confirm-archive-btn"
              >
                {archiving ? 'Archiviando...' : '📥 Archivia'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default { SchedaGiocatoreV2, StatisticheTorneoV2, DashboardStatsClubV2, AlboOro };
